import React from 'react';
import { useProfile } from '../hooks/useProfile';
import { useCheckout } from '../hooks/useCheckout';

const Upgrade: React.FC = () => {
    const { profile } = useProfile();
    const { redirectToCheckout, loading } = useCheckout();
    const [isLifetime, setIsLifetime] = React.useState(true);

    const currentPlan = profile?.plan || 'free';

    return (
        <div className="min-h-screen bg-[#0F172A] text-white">
            <div className="container-page pt-32 pb-20 px-4">
                {/* Header */}
                <div className="text-center mb-16">
                    <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-4 text-white">Pricing for Every Stage of Your Journey</h1>
                    <p className="text-dark-400 text-xl font-medium">Simple plans that scale with your creativity. Ship more, spend less.</p>
                </div>

                {/* Pricing Toggle */}
                <div className="flex justify-center mb-16">
                    <div className="bg-dark-900 border border-white/5 p-1.5 rounded-2xl flex items-center shadow-2xl backdrop-blur-sm">
                        <button
                            onClick={() => setIsLifetime(false)}
                            className={`px-8 py-3 rounded-xl text-sm font-bold transition-all ${!isLifetime ? 'bg-white text-dark-950 shadow-glow-white' : 'text-dark-400 hover:text-white'}`}
                        >
                            Monthly
                        </button>
                        <button
                            onClick={() => setIsLifetime(true)}
                            className={`px-8 py-3 rounded-xl text-sm font-bold transition-all flex items-center gap-2 ${isLifetime ? 'bg-primary-500 text-white shadow-glow' : 'text-dark-400 hover:text-white'}`}
                        >
                            Lifetime
                            <span className={`text-[10px] px-2 py-0.5 rounded-full border font-black ${isLifetime ? 'bg-white/20 border-white/30 text-white' : 'bg-primary-500 text-white border-primary-400 shadow-glow'}`}>
                                -50%
                            </span>
                        </button>
                    </div>
                </div>

                <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                    {/* Free Plan */}
                    <div className="group p-8 rounded-[40px] bg-dark-900 border border-white/5 hover:border-white/10 transition-all duration-300 flex flex-col">
                        <div className="mb-8">
                            <h3 className="text-xl font-bold text-white mb-2">Free</h3>
                            <div className="flex items-baseline gap-1">
                                <span className="text-4xl font-black text-white">$0</span>
                                <span className="text-dark-500 font-bold uppercase tracking-widest text-[10px]">/month</span>
                            </div>
                        </div>
                        <ul className="space-y-4 mb-10 flex-1 text-sm">
                            {[
                                { text: '10 images per day', icon: 'check' },
                                { text: 'All fonts & colors', icon: 'check' },
                                { text: 'Download as PNG', icon: 'check' },
                                { text: 'Watermark on images', icon: 'cross' },
                            ].map((item, i) => (
                                <li key={i} className={`flex items-center gap-3 font-medium ${item.icon === 'cross' ? 'text-dark-500' : 'text-dark-300'}`}>
                                    {item.icon === 'check' ? (
                                        <svg className="w-5 h-5 text-success-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                        </svg>
                                    ) : (
                                        <svg className="w-5 h-5 text-danger-500/70 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    )}
                                    {item.text}
                                </li>
                            ))}
                        </ul>
                        <div className="w-full py-4 rounded-2xl bg-dark-800 text-dark-400 font-black text-center text-sm border border-white/5 cursor-default">
                            {currentPlan === 'free' ? 'Your Current Plan' : 'Free Tier'}
                        </div>
                    </div>

                    {/* Creator Plan */}
                    <div className="group p-8 rounded-[40px] bg-dark-900 border border-white/10 hover:border-primary-500/30 transition-all duration-300 flex flex-col relative overflow-hidden ring-1 ring-white/5">
                        <div className="mb-8">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-xl font-bold text-white">Creator</h3>
                                <span className="text-[10px] font-black uppercase tracking-widest text-orange-500 animate-pulse">🔥 Launch Price</span>
                            </div>
                            <div className="flex items-baseline gap-2">
                                <span className="text-xl text-dark-500 line-through font-bold"> $12 </span>
                                <span className="text-4xl font-black text-white">$5</span>
                                <span className="text-dark-500 font-bold uppercase tracking-widest text-[10px]">/month</span>
                            </div>
                        </div>
                        <ul className="space-y-4 mb-10 flex-1 text-sm">
                            {[
                                'Unlimited generations',
                                'No watermark',
                                'All premium fonts',
                                'Save to dashboard',
                                'Priority Support',
                            ].map((item, i) => (
                                <li key={i} className="flex items-center gap-3 text-dark-100 font-medium">
                                    <svg className="w-5 h-5 text-primary-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                    </svg>
                                    {item}
                                </li>
                            ))}
                        </ul>
                        {currentPlan === 'creator' ? (
                            <div className="w-full py-4 rounded-2xl bg-primary-500/10 border border-primary-500/30 text-primary-400 font-black text-center text-sm cursor-default">
                                ✅ Active Subscription
                            </div>
                        ) : (
                            <button
                                onClick={() => redirectToCheckout('creator')}
                                disabled={loading}
                                className="w-full py-4 rounded-2xl border-2 border-primary-500/50 text-white font-black text-center text-sm hover:bg-primary-500 transition-all active:scale-95 shadow-glow/20"
                            >
                                {loading ? 'Processing...' : 'Start Shipping Now'}
                            </button>
                        )}
                    </div>

                    {/* Pro or Lifetime Plan */}
                    {!isLifetime ? (
                        <div className="group p-8 rounded-[40px] bg-dark-900 border border-primary-500/30 hover:border-primary-500/60 transition-all duration-300 flex flex-col relative overflow-hidden ring-1 ring-primary-500/20 shadow-glow">
                            <div className="absolute top-0 right-0 bg-primary-500 text-white px-5 py-2 rounded-bl-3xl text-[10px] font-black uppercase tracking-widest shadow-xl z-10">
                                Most Popular
                            </div>
                            <div className="mb-8">
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className="text-xl font-bold text-primary-400">Pro</h3>
                                    <span className="text-[10px] font-black uppercase tracking-widest text-orange-500 animate-pulse">🔥 Launch Price</span>
                                </div>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-xl text-dark-500 line-through font-bold"> $25 </span>
                                    <span className="text-4xl font-black text-white">$15</span>
                                    <span className="text-dark-500 font-bold uppercase tracking-widest text-[10px]">/month</span>
                                </div>
                            </div>
                            <ul className="space-y-4 mb-10 flex-1 text-sm">
                                {[
                                    'Everything in Creator',
                                    'Batch CSV generation',
                                    'Download all as ZIP',
                                    'Brand Kits (Coming)',
                                    'Custom Logos',
                                ].map((item, i) => (
                                    <li key={i} className="flex items-center gap-3 text-white font-medium">
                                        <svg className="w-5 h-5 text-primary-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                        </svg>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                            {currentPlan === 'pro' ? (
                                <div className="w-full py-4 rounded-2xl bg-primary-500/10 border border-primary-500/30 text-primary-400 font-black text-center text-sm cursor-default">
                                    ✅ Current Plan
                                </div>
                            ) : (
                                <button
                                    onClick={() => redirectToCheckout('pro')}
                                    disabled={loading}
                                    className="w-full py-4 rounded-2xl bg-primary-500 text-white font-black text-center text-sm hover:bg-primary-600 transition-all active:scale-95 shadow-glow"
                                >
                                    {loading ? 'Processing...' : 'Start Shipping Now'}
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="group p-8 rounded-[40px] bg-dark-950 border-2 border-primary-500/50 hover:border-primary-500 transition-all duration-500 flex flex-col relative overflow-hidden shadow-[0_0_60px_rgba(124,58,237,0.3)] scale-[1.02] z-10">
                            <div className="absolute top-0 right-0 bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-6 py-2 rounded-bl-3xl text-[10px] font-black uppercase tracking-widest shadow-xl">
                                Top Value
                            </div>
                            <div className="mb-8">
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className="text-xl font-bold text-white">Lifetime Access</h3>
                                    <span className="text-[10px] font-black uppercase tracking-widest text-orange-500 animate-pulse">🔥 Founder's Deal</span>
                                </div>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-2xl text-dark-500 line-through font-bold"> $79 </span>
                                    <span className="text-5xl font-black text-white">$39</span>
                                    <span className="text-dark-500 font-bold uppercase tracking-widest text-[10px]">One-time</span>
                                </div>
                            </div>
                            <ul className="space-y-4 mb-10 flex-1 text-sm">
                                {[
                                    'Everything in Pro',
                                    'Pay once, use forever',
                                    'All future updates',
                                    'Founder Badge',
                                    'Private Community',
                                ].map((item, i) => (
                                    <li key={i} className="flex items-center gap-3 text-white font-semibold">
                                        <svg className="w-5 h-5 text-secondary-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                        </svg>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                            {currentPlan === 'lifetime' ? (
                                <div className="w-full py-5 rounded-2xl bg-secondary-500/10 border border-secondary-500/30 text-secondary-400 font-black text-center text-lg cursor-default">
                                    ⭐ Lifetime Holder
                                </div>
                            ) : (
                                <button
                                    onClick={() => redirectToCheckout('lifetime')}
                                    disabled={loading}
                                    className="w-full py-5 rounded-2xl bg-white text-dark-950 font-black text-center text-lg hover:bg-white/90 hoverScale transition-all shadow-glow-white"
                                >
                                    {loading ? 'Processing...' : 'Start Shipping Now'}
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Trust Signals */}
                <div className="mt-20 flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-80 animate-fade-in mb-10">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary-500/10 flex items-center justify-center">
                            <svg className="w-5 h-5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                        </div>
                        <span className="text-white/80 font-bold text-sm">500+ founders ship with us</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-secondary-500/10 flex items-center justify-center">
                            <svg className="w-5 h-5 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                            </svg>
                        </div>
                        <span className="text-white/80 font-bold text-sm">Loved by the #BuildInPublic community</span>
                    </div>
                </div>

                {/* FAQ / Support link */}
                <div className="text-center">
                    <p className="text-dark-500 text-sm">
                        Need help or have questions about our plans?{' '}
                        <a href="mailto:hello@sharevisuals.com" className="text-primary-400 hover:text-primary-300 font-bold underline">
                            Contact our team
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Upgrade;
